import { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  Avatar,
  Drawer,
  Grid,
  Paper,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Storage as StorageIcon,
  Language as WebsiteIcon,
  Dashboard as GrafanaDashboardIcon,
  BarChart as ChartsIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Shield as ShieldIcon,
  Cloud as CloudIcon,
  Apps as AppsIcon,
  Dns as DnsIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { StatusChip, SeverityChip } from '../../../../../components/shared';
import {
  DeviceDetailDrawerProps,
  AlertHistoryItem,
  getCardBorder,
  CARD_BORDER_RADIUS,
  GRAFANA_DASHBOARD_MAP,
} from '../types';
import { GrafanaPreview } from './GrafanaPreview';
import { MetricsPreview } from './MetricsPreview';
import { AlertDetailModal } from './AlertDetailModal';
import { useGrafana } from '../../hooks/useGrafana';
import { WebsiteHistoryComponent } from '../../components/WebsiteHistory';
import { Website } from '../../types/website';

type DashboardViewMode = 'charts' | 'grafana';

/**
 * DeviceDetailDrawer - Slide-out drawer showing detailed device information.
 *
 * Features:
 * - Device header with status and client info
 * - Active alerts banner
 * - Device details grid (name, host, category, etc.)
 * - Grafana dashboard preview
 * - Alert history table (clickable rows open AlertDetailModal)
 * - Fullscreen Grafana modal
 */
export function DeviceDetailDrawer({
  device,
  open,
  onClose,
  alertHistory = [],
  onSilenceAlert,
}: DeviceDetailDrawerProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { orgId } = useGrafana();
  const navigate = useNavigate();

  const [selectedAlert, setSelectedAlert] = useState<AlertHistoryItem | null>(null);
  const [grafanaFullscreen, setGrafanaFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState<DashboardViewMode>('charts');

  const handleAlertClick = (alert: AlertHistoryItem) => {
    setSelectedAlert(alert);
  };

  const handleCloseAlertModal = () => {
    setSelectedAlert(null);
  };

  const handleClose = () => {
    setSelectedAlert(null);
    setGrafanaFullscreen(false);
    onClose();
  };

  if (!device) return null;

  // Build Grafana URL based on device's dashboard UID or prometheus job
  const dashboardUid =
    device.grafanaDashboardUid ||
    GRAFANA_DASHBOARD_MAP[device.prometheusJob || ''] ||
    null;

  const getGrafanaUrl = (): string | null => {
    if (!dashboardUid) return null;
    const resolvedOrgId = orgId ?? 1;
    return `/grafana/d/${dashboardUid}?orgId=${resolvedOrgId}&kiosk&theme=light&var-server=${encodeURIComponent(
      device.host || device.name
    )}`;
  };

  const grafanaUrl = getGrafanaUrl();
  const hasActiveAlerts = device.alertCount > 0;
  const isWebsite = !!device.url;

  // Get device type icon from DB category name
  const getDeviceIcon = () => {
    switch (device.type) {
      case 'Infrastructure':
        return <ComputerIcon />;
      case 'Database':
        return <StorageIcon />;
      case 'Network':
        return <DnsIcon />;
      case 'Security':
        return <ShieldIcon />;
      case 'Virtualization':
        return <CloudIcon />;
      case 'Application':
        return <AppsIcon />;
      case 'Web':
        return <WebsiteIcon />;
      default:
        return <ComputerIcon />;
    }
  };

  const getAvatarColors = () => {
    if (device.status === 'up') {
      return {
        bgcolor: alpha(theme.palette.success.main, 0.1),
        color: 'success.main',
      };
    }
    if (device.status === 'down') {
      return {
        bgcolor: alpha(theme.palette.error.main, 0.1),
        color: 'error.main',
      };
    }
    return {
      bgcolor: alpha(theme.palette.warning.main, 0.1),
      color: 'warning.main',
    };
  };

  const avatarColors = getAvatarColors();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        aria-label="Device details"
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '85%', md: '70%', lg: '60%' },
            maxWidth: 900,
          },
        }}
      >
        {/* Device Header */}
        <Box
          sx={{
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: getCardBorder(theme),
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isMobile && (
              <IconButton onClick={handleClose} edge="start" aria-label="Close device details">
                <ArrowBackIcon />
              </IconButton>
            )}
            <Avatar
              sx={{
                width: 48,
                height: 48,
                ...avatarColors,
              }}
            >
              {getDeviceIcon()}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {device.displayName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {device.name}
                </Typography>
                <Chip
                  label={device.client}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem' }}
                />
                <StatusChip status={device.status} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {!isWebsite && (
              <Tooltip title="Manage in Settings">
                <IconButton
                  size="small"
                  onClick={() => {
                    handleClose();
                    navigate(`/sense/management?editExporter=${device.id}`);
                  }}
                  aria-label="Manage device in settings"
                >
                  <SettingsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!isMobile && (
              <IconButton onClick={handleClose} aria-label="Close device details">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Active Alerts Banner */}
        {hasActiveAlerts && (
          <Box
            sx={{
              mx: 2,
              mt: 2,
              p: 1.5,
              bgcolor: 'error.main',
              borderRadius: CARD_BORDER_RADIUS,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ErrorIcon sx={{ color: 'white' }} />
            <Typography variant="body2" sx={{ color: 'white', flex: 1 }}>
              {device.alertCount} active alert{device.alertCount > 1 ? 's' : ''} on this
              device
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={() => {
                const activeAlert = alertHistory.find((a) => a.status === 'active');
                if (activeAlert) {
                  handleAlertClick(activeAlert);
                }
              }}
              sx={{
                bgcolor: 'white',
                color: 'error.main',
                borderRadius: '999px',
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              View Alerts
            </Button>
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: 2, overflow: 'auto' }}>
          {isWebsite && device.websiteId ? (
            /* Website: show rich monitoring history */
            <WebsiteHistoryComponent website={{ id: device.websiteId } as Website} />
          ) : (
            <Grid container spacing={2}>
              {/* Left Column: Device Details */}
              <Grid item xs={12} md={5}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: getCardBorder(theme),
                    borderRadius: CARD_BORDER_RADIUS,
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
                    Device Details
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Display Name
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {device.displayName}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Customer
                      </Typography>
                      <Typography variant="body2">{device.client}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Category
                      </Typography>
                      <Chip
                        label={device.type}
                        size="small"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Exporter Type
                      </Typography>
                      <Chip
                        label={device.category}
                        size="small"
                        color="primary"
                        variant="outlined"
                        sx={{ mt: 0.5 }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Device Name
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem" sx={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                        {device.name}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Target Host
                      </Typography>
                      <Typography variant="body2" fontFamily="monospace" fontSize="0.85rem" sx={{ wordBreak: 'break-all', overflowWrap: 'break-word' }}>
                        {device.host}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Prometheus Job
                      </Typography>
                      <Typography variant="body2">{device.prometheusJob || device.category}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Collection Method
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={device.collectionMethod === 'push' ? 'Push (Alloy)' : 'Pull (Scrape)'}
                          size="small"
                          color={device.collectionMethod === 'push' ? 'info' : 'default'}
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <StatusChip status={device.status} />
                        <Chip label="Enabled" size="small" color="success" variant="outlined" />
                        {device.collectionMethod === 'push' && (
                          <Chip label="Push" size="small" variant="outlined" color="info" />
                        )}
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">
                        {device.collectionMethod === 'push' ? 'Last Data Received' : 'Last Scrape'}
                      </Typography>
                      <Typography variant="body2">{device.lastSeen}</Typography>
                    </Grid>
                  </Grid>

                </Paper>
              </Grid>

              {/* Right Column: Charts / Grafana Toggle */}
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                  <ToggleButtonGroup
                    value={viewMode}
                    exclusive
                    onChange={(_, v) => v && setViewMode(v as DashboardViewMode)}
                    size="small"
                    sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.25, fontSize: '0.75rem' } }}
                  >
                    <ToggleButton value="charts">
                      <ChartsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Charts
                    </ToggleButton>
                    <ToggleButton value="grafana">
                      <GrafanaDashboardIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      Grafana
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
                {viewMode === 'charts' ? (
                  <MetricsPreview device={device} grafanaUrl={grafanaUrl} />
                ) : grafanaUrl ? (
                  <GrafanaPreview
                    url={grafanaUrl}
                    deviceName={device.displayName}
                    height={isMobile ? 300 : isTablet ? 400 : 500}
                    showPlaceholder={false}
                    onFullscreen={() => setGrafanaFullscreen(true)}
                  />
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      border: getCardBorder(theme),
                      borderRadius: CARD_BORDER_RADIUS,
                      height: isMobile ? 300 : isTablet ? 400 : 500,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="body2">
                      No dashboard available for this exporter type
                    </Typography>
                  </Paper>
                )}
              </Grid>

              {/* Alert History - Full Width */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    border: getCardBorder(theme),
                    borderRadius: CARD_BORDER_RADIUS,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      borderBottom: getCardBorder(theme),
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <HistoryIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight="bold">
                        Alert History
                      </Typography>
                      <Chip label={alertHistory.length} size="small" />
                    </Box>
                  </Box>

                  {alertHistory.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 40, mb: 1 }} />
                      <Typography color="text.secondary">
                        No alerts recorded for this device
                      </Typography>
                    </Box>
                  ) : (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Alert</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Time</TableCell>
                            <TableCell>Duration</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {alertHistory.map((alert) => (
                            <TableRow
                              key={alert.id}
                              hover
                              onClick={() => handleAlertClick(alert)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Typography variant="body2">{alert.name}</Typography>
                              </TableCell>
                              <TableCell>
                                <SeverityChip severity={alert.severity} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={alert.status}
                                  size="small"
                                  color={alert.status === 'active' ? 'error' : 'default'}
                                  variant={alert.status === 'active' ? 'filled' : 'outlined'}
                                  sx={{ textTransform: 'capitalize', fontSize: '0.65rem' }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.time}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {alert.duration}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Grafana Fullscreen Modal */}
        <Dialog
          open={grafanaFullscreen && !!grafanaUrl}
          onClose={() => setGrafanaFullscreen(false)}
          maxWidth="xl"
          fullWidth
          aria-label="Grafana dashboard fullscreen"
          PaperProps={{
            sx: {
              borderRadius: CARD_BORDER_RADIUS,
              height: '90vh',
              maxHeight: '90vh',
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 1.5,
              borderBottom: getCardBorder(theme),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GrafanaDashboardIcon color="primary" />
              <Typography variant="subtitle1" fontWeight="bold">
                {device.displayName} - Dashboard
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={() => setGrafanaFullscreen(false)} aria-label="Close fullscreen dashboard">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Grafana Iframe - Full Size */}
          <Box
            sx={{
              flex: 1,
              bgcolor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <iframe
              src={grafanaUrl || ''}
              title={`Grafana Dashboard - ${device.displayName}`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-pointer-lock allow-downloads"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            />
          </Box>
        </Dialog>
      </Drawer>

      {/* Alert Detail Modal */}
      <AlertDetailModal
        alert={selectedAlert}
        open={selectedAlert !== null}
        onClose={handleCloseAlertModal}
        onSilence={onSilenceAlert}
      />
    </>
  );
}

export default DeviceDetailDrawer;
